import type { ReactNode } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArtifactFormSkeleton } from '@/components/layout/artifact-form-skeleton'
import { CheckCircle2, FileText, Loader2 } from 'lucide-react'

import {
  useExternalApplyContext,
  useExternalApplyForm,
} from '@/components/external-apply/external-apply-form-state'
import { ExternalApplyFields } from '@/components/external-apply/external-apply-start-form'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { EntityAboutSection } from '@/components/ui/entity-about-section'
import { TenantLogoImg } from '@/components/tenant-logo-img'
import { API_ORIGIN, AGENT_UI_ENABLED, CURRENT_CLIENT } from '@/constants'
import { LABS_AGENT_MAGIC_LINK_KICKOFF_KEY, MAGIC_LINK_AGENT_KICKOFF_MESSAGE } from '@/constants/agent-kickoff'
import { usePublicShareRouteParam } from '@/lib/public-share-route-param'

export function ExternalApplyPage({ jobIdOverride }: { jobIdOverride?: string } = {}) {
  const jobId = usePublicShareRouteParam('/apply/:jobId', 'jobId', jobIdOverride)
  const contextState = useExternalApplyContext(jobId || undefined)
  const context = contextState.status === 'ready' ? contextState.context : null
  const form = useExternalApplyForm(jobId || undefined, context)

  if (!jobId) {
    return (
      <PublicApplyShell>
        <Card className="w-full max-w-2xl">
          <CardHeader>
            <CardTitle>Application unavailable</CardTitle>
            <CardDescription alwaysShow>Could not load this job</CardDescription>
          </CardHeader>
        </Card>
      </PublicApplyShell>
    )
  }

  if (contextState.status === 'loading' || contextState.status === 'idle') {
    return (
      <PublicApplyShell>
        <ArtifactFormSkeleton fields={4} ariaLabel="Loading application form" />
      </PublicApplyShell>
    )
  }

  if (contextState.status === 'missing' || contextState.status === 'error' || !context) {
    const message = contextState.status === 'ready' ? 'Could not load this job' : contextState.message
    return (
      <PublicApplyShell>
        <Card className="w-full max-w-2xl">
          <CardHeader>
            <CardTitle>Application unavailable</CardTitle>
            <CardDescription alwaysShow>{message}</CardDescription>
          </CardHeader>
        </Card>
      </PublicApplyShell>
    )
  }

  if (form.submittedEmail) {
    return (
      <PublicApplyShell>
        <Card className="w-full max-w-2xl" role="status" aria-live="polite">
          <CardHeader>
            <div className="flex items-center gap-2 text-primary">
              <CheckCircle2 className="h-5 w-5" />
              <CardTitle>Check your email</CardTitle>
            </div>
            <CardDescription alwaysShow>
              We sent a secure link to {form.submittedEmail}. Use it to finish your application for {context.opportunity.title}.
            </CardDescription>
          </CardHeader>
        </Card>
      </PublicApplyShell>
    )
  }

  return (
    <PublicApplyShell>
      <EntityAboutSection
        title="Job description"
        leadingIcon={FileText}
        subtitle="Full text for this role"
        body={context.opportunity.description}
        stripLeadingTitles={[context.opportunity.title]}
        emptyVariant="text"
        emptyMessage="No description provided."
        className="w-full max-w-3xl"
      />
      <Card className="w-full max-w-3xl">
        <CardHeader>
          <CardTitle>{context.opportunity.title}</CardTitle>
          <CardDescription alwaysShow>
            {context.opportunity.organisation_name}
            {context.opportunity.location ? ` · ${context.opportunity.location}` : ''}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ExternalApplyFields form={form} />
        </CardContent>
      </Card>
    </PublicApplyShell>
  )
}

type CompleteStatus = 'verifying' | 'ready' | 'completing' | 'complete' | 'error'

type VerifyDraft = {
  email?: string
  first_name?: string
  last_name?: string
  cover_letter?: string
  has_resume?: boolean
}

async function readJson(response: Response): Promise<Record<string, unknown>> {
  try {
    const data = await response.json()
    if (data && typeof data === 'object') return data as Record<string, unknown>
  } catch {
    /* HTML 404 pages and empty bodies */
  }
  return {}
}

function errorText(data: Record<string, unknown>, fallback: string): string {
  return typeof data.error === 'string' && data.error.trim() ? data.error : fallback
}

function isVerifyDraft(value: unknown): value is VerifyDraft {
  return Boolean(value) && typeof value === 'object'
}

export function ExternalApplyCompletePage({ tokenOverride }: { tokenOverride?: string } = {}) {
  const token = usePublicShareRouteParam('/apply/complete/:token', 'token', tokenOverride)
  const [status, setStatus] = useState<CompleteStatus>('verifying')
  const [legacy, setLegacy] = useState(false)
  const [message, setMessage] = useState('Checking your application link...')
  const [draft, setDraft] = useState<VerifyDraft | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const completeStarted = useRef(false)

  const finishApplication = useCallback(
    async (mode: 'legacy' | 'submit') => {
      if (!token || completeStarted.current) return
      completeStarted.current = true
      setLegacy(mode === 'legacy')
      setStatus('completing')
      setSubmitError(null)
      if (mode === 'legacy') setMessage('Finishing your application...')
      try {
        const response = await fetch(
          `${API_ORIGIN}/api/applications/external/apply/complete/${encodeURIComponent(token)}`,
        )
        const data = await readJson(response)
        if (!response.ok) throw new Error(errorText(data, 'Could not finish this application'))
        if (typeof data.access_token === 'string' && data.access_token) {
          localStorage.setItem('auth_token', data.access_token)
          localStorage.setItem('auth_user', JSON.stringify(data.user ?? {}))
        }
        if (AGENT_UI_ENABLED) {
          try {
            sessionStorage.setItem(
              LABS_AGENT_MAGIC_LINK_KICKOFF_KEY,
              JSON.stringify({ message: MAGIC_LINK_AGENT_KICKOFF_MESSAGE }),
            )
          } catch {
            /* ignore */
          }
        }
        setStatus('complete')
        setMessage('Your application has been submitted.')
        window.setTimeout(() => {
          window.location.href = AGENT_UI_ENABLED ? '/' : '/candidate/jobs?tab=applied'
        }, 1200)
      } catch (err) {
        const text = err instanceof Error ? err.message : 'Could not finish this application'
        if (mode === 'legacy') {
          setStatus('error')
          setMessage(text)
          return
        }
        completeStarted.current = false
        setStatus('ready')
        setSubmitError(text)
      }
    },
    [token],
  )

  useEffect(() => {
    let cancelled = false
    const timer = window.setTimeout(() => {
      void (async () => {
        if (!token) {
          if (!cancelled) {
            setStatus('error')
            setMessage('Could not finish this application')
          }
          return
        }
        try {
          const response = await fetch(
            `${API_ORIGIN}/api/applications/external/apply/verify/${encodeURIComponent(token)}`,
          )
          if (cancelled) return
          if (response.status === 404) {
            await finishApplication('legacy')
            return
          }
          const data = await readJson(response)
          if (cancelled) return
          if (!response.ok) {
            setStatus('error')
            setMessage(errorText(data, 'Could not finish this application'))
            return
          }
          setDraft(isVerifyDraft(data.draft) ? data.draft : null)
          const readyMessage =
            typeof data.message === 'string' && data.message.trim()
              ? data.message
              : 'Review your application, then submit.'
          setMessage(readyMessage)
          setStatus('ready')
        } catch (err) {
          if (cancelled) return
          setStatus('error')
          setMessage(err instanceof Error ? err.message : 'Could not finish this application')
        }
      })()
    }, 0)
    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [finishApplication, token])

  const draftName = [draft?.first_name, draft?.last_name].filter(Boolean).join(' ')
  const showReady = status === 'ready' || (status === 'completing' && !legacy)

  return (
    <PublicApplyShell>
      {showReady ? (
        <Card className="w-full max-w-2xl">
          <CardHeader>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-primary" />
              <CardTitle id="apply-ready-title">Ready</CardTitle>
            </div>
            <CardDescription alwaysShow>{message}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {draftName || draft?.email ? (
              <dl className="space-y-2 text-sm">
                {draftName ? (
                  <div>
                    <dt className="text-muted-foreground">Name</dt>
                    <dd className="font-medium text-foreground">{draftName}</dd>
                  </div>
                ) : null}
                {draft?.email ? (
                  <div>
                    <dt className="text-muted-foreground">Email</dt>
                    <dd className="font-medium text-foreground">{draft.email}</dd>
                  </div>
                ) : null}
              </dl>
            ) : null}
            {submitError ? (
              <p className="text-sm text-destructive" role="alert">
                {submitError}
              </p>
            ) : null}
            <Button type="button" onClick={() => void finishApplication('submit')} disabled={status === 'completing'}>
              {status === 'completing' && <Loader2 className="h-4 w-4 animate-spin" />}
              Submit
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card
          className="w-full max-w-2xl"
          role={status === 'error' ? 'alert' : 'status'}
          aria-live="polite"
          aria-busy={status === 'verifying' || status === 'completing'}
        >
          <CardHeader>
            <div className="flex items-center gap-2">
              {status === 'verifying' || status === 'completing' ? (
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              ) : status === 'complete' ? (
                <CheckCircle2 className="h-5 w-5 text-primary" />
              ) : null}
              <CardTitle>
                {status === 'error'
                  ? 'Application link problem'
                  : status === 'verifying'
                    ? 'Checking your application link'
                    : 'Completing application'}
              </CardTitle>
            </div>
            <CardDescription alwaysShow>{message}</CardDescription>
          </CardHeader>
          {status === 'error' && (
            <CardContent>
              <Button asChild variant="outline">
                <Link to="/">Return to {CURRENT_CLIENT.name}</Link>
              </Button>
            </CardContent>
          )}
        </Card>
      )}
    </PublicApplyShell>
  )
}

function PublicApplyShell({ children }: { children: ReactNode }) {
  return (
    <main className="min-h-screen bg-background px-4 py-8 text-foreground">
      <div className="mx-auto flex min-h-[calc(100vh-4rem)] max-w-5xl flex-col items-center justify-center gap-6">
        <div className="flex flex-col items-center gap-3 text-center">
          <TenantLogoImg
            logoOnLightBackground
            className="inline-block h-10 max-w-[220px] aspect-[123/27] bg-foreground"
          />
          <p className="text-sm font-medium text-muted-foreground">{CURRENT_CLIENT.name}</p>
          <h1 className="text-2xl font-semibold">Apply for this role</h1>
        </div>
        {children}
      </div>
    </main>
  )
}
